"use client";

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";

import { useRequiredAccountStore } from "@/app/_components/required/create-account";
import { useToast } from "@/components/ui/use-toast";

import { handleApiResponse } from "@/lib/errors";

import { saveFavorite } from "@/app/_actions/favorites/post-favorite";

import { queryKeys } from "../query-key";

interface Props {
  initialFavorite: boolean;
  revalidateMainListPath?: boolean;
}

export const usePostFavorite = ({
  initialFavorite,
  revalidateMainListPath,
}: Props) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { toggle } = useRequiredAccountStore();
  const [isFavorite, setIsFavorite] = useState(initialFavorite);
  const { isSignedIn } = useAuth();

  const mutation = useMutation({
    mutationFn: async (leilaoId: string) => {
      const res = await saveFavorite({ leilaoId, revalidateMainListPath });
      return handleApiResponse(res) as { favorito: boolean };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites });
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding });

      toast({
        variant: "default",
        title: data.favorito
          ? "Imóvel adicionado aos favoritos!"
          : "Imóvel removido dos favoritos!",
      });
      setIsFavorite(data.favorito);
    },
    onError: (error) => {
      toast({
        title: "Erro ao adicionar imóvel aos favoritos",
        description:
          error instanceof Error
            ? error.message
            : "Ocorreu um erro ao adicionar o imóvel aos favoritos",
        variant: "destructive",
      });
    },
  });

  const save = (id: string) => {
    if (isSignedIn) {
      return mutation.mutate(id);
    } else {
      toggle();
    }
  };

  return { save, isPending: mutation.isPending, isFavorite };
};
